import { useEffect, useMemo, useState } from 'react';
import { Loader2, Save, Plus, Trash2, Search, X } from 'lucide-react';
import { toast } from 'sonner';
import { adminApi } from '../lib/adminApi';

const inputCls = 'border border-[#eaeaea] focus:border-[#eb5381] outline-none px-3 py-2 text-sm';

export default function AdminLocations() {
  const [locations, setLocations] = useState(null);
  const [saving, setSaving] = useState(false);
  const [q, setQ] = useState('');
  const [newState, setNewState] = useState('');
  const [newCity, setNewCity] = useState({});

  useEffect(() => { adminApi.getLocations().then(d => setLocations(d.locations || [])).catch(() => toast.error('Could not load locations')); }, []);

  const save = async (next) => {
    setLocations(next);
    setSaving(true);
    try { await adminApi.saveLocations(next); toast.success('Locations saved'); }
    catch { toast.error('Save failed'); }
    finally { setSaving(false); }
  };

  const addState = () => {
    const v = newState.trim(); if (!v) return;
    if (locations.some(s => s.state.toLowerCase() === v.toLowerCase())) { toast.error('State exists'); return; }
    save([...locations, { state: v, active: true, cities: [] }].sort((a, b) => a.state.localeCompare(b.state)));
    setNewState('');
  };
  const toggleState = (i) => save(locations.map((s, x) => x === i ? { ...s, active: !s.active } : s));
  const removeState = (i) => save(locations.filter((_, x) => x !== i));
  const addCity = (i) => {
    const v = (newCity[i] || '').trim(); if (!v) return;
    if (locations[i].cities.some(c => c.toLowerCase() === v.toLowerCase())) { toast.error('City exists'); return; }
    save(locations.map((s, x) => x === i ? { ...s, cities: [...s.cities, v].sort() } : s));
    setNewCity(n => ({ ...n, [i]: '' }));
  };
  const removeCity = (i, city) => save(locations.map((s, x) => x === i ? { ...s, cities: s.cities.filter(c => c !== city) } : s));

  const filtered = useMemo(() => {
    if (!locations) return [];
    const term = q.trim().toLowerCase();
    if (!term) return locations.map((s, i) => ({ ...s, _i: i }));
    return locations.map((s, i) => ({ ...s, _i: i }))
      .filter(s => s.state.toLowerCase().includes(term) || s.cities.some(c => c.toLowerCase().includes(term)));
  }, [locations, q]);

  if (!locations) return <div className="py-16 flex justify-center text-neutral-400"><Loader2 className="w-5 h-5 animate-spin" /></div>;

  return (
    <div data-testid="admin-locations-page" className="max-w-4xl">
      <div className="text-[11px] uppercase tracking-[0.3em] text-[#eb5381]">Coverage</div>
      <h1 className="mt-1 font-serif text-3xl md:text-4xl">Locations</h1>
      <p className="mt-2 text-sm text-neutral-600">States/UTs and their cities. Changes sync automatically to the registration form and the Browse city filter. Deactivating a state hides it everywhere; existing artist locations are preserved.</p>

      <div className="mt-6 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
          <input className={`${inputCls} w-full pl-9`} placeholder="Search state or city" value={q} onChange={e => setQ(e.target.value)} data-testid="location-search" />
        </div>
        <div className="flex gap-2">
          <input className={inputCls} placeholder="Add state / UT" value={newState} onChange={e => setNewState(e.target.value)} onKeyDown={e => e.key === 'Enter' && addState()} data-testid="add-state-input" />
          <button onClick={addState} data-testid="add-state-btn" className="inline-flex items-center gap-1 bg-[#eb5381] text-white px-4 text-[10px] uppercase tracking-[0.2em]"><Plus className="w-3.5 h-3.5" /> State</button>
        </div>
        {saving && <Loader2 className="w-4 h-4 animate-spin text-[#eb5381]" />}
      </div>

      <div className="mt-6 space-y-3">
        {filtered.map((s) => (
          <div key={s.state} data-testid={`state-${s._i}`} className={`border p-4 ${s.active ? 'border-[#eaeaea]' : 'border-dashed border-neutral-300 bg-neutral-50'}`}>
            <div className="flex items-center gap-3 mb-3">
              <h3 className={`font-serif text-xl flex-1 ${s.active ? '' : 'text-neutral-400'}`}>{s.state} <span className="text-xs text-neutral-400">({s.cities.length})</span></h3>
              <button onClick={() => toggleState(s._i)} className="text-[10px] uppercase tracking-[0.15em] text-neutral-500 hover:text-[#eb5381]">{s.active ? 'Deactivate' : 'Activate'}</button>
              <button onClick={() => removeState(s._i)} className="text-neutral-400 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
            </div>
            <div className="flex flex-wrap gap-2 mb-3">
              {s.cities.map(c => (
                <span key={c} className="inline-flex items-center gap-1 border border-[#eaeaea] px-2 py-1 text-xs">
                  {c}
                  <button onClick={() => removeCity(s._i, c)} className="text-neutral-400 hover:text-red-500"><X className="w-3 h-3" /></button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <input className={`${inputCls} flex-1`} placeholder={`Add city to ${s.state}`} value={newCity[s._i] || ''} onChange={e => setNewCity(n => ({ ...n, [s._i]: e.target.value }))} onKeyDown={e => e.key === 'Enter' && addCity(s._i)} data-testid={`add-city-input-${s._i}`} />
              <button onClick={() => addCity(s._i)} data-testid={`add-city-btn-${s._i}`} className="border border-[#eaeaea] hover:border-[#eb5381] px-3 text-[10px] uppercase tracking-[0.2em]"><Plus className="w-3.5 h-3.5" /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
